import React, { useState, useEffect, useCallback } from 'react';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { RefreshCw, AlertCircle, Download, FileText, X, Filter } from 'lucide-react';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

// ============================================================================
// CUT AND SOLD DASHBOARD - Anne Cole Division 60 Only
// ============================================================================
export default function AnneColeCutAndSoldDashboard() {
  // Data state
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [presoldData, setPresoldData] = useState([]);
  const [openStockData, setOpenStockData] = useState([]);
  const [ssdData, setSsdData] = useState([]);
  const [stockByCustomerData, setStockByCustomerData] = useState([]);
  const [totals, setTotals] = useState({});
  const [filters, setFilters] = useState({ seasons: [], customers: [], labels: [], divisions: [] });

  // Filter state - Anne Cole Division 60
  const [selectedSeason, setSelectedSeason] = useState('SP26');
  // Excluded labels (clicking a label excludes it)
  const [excludedLabels, setExcludedLabels] = useState([]);

  // Cross-filter selections (Power BI style)
  const [selectedPresold, setSelectedPresold] = useState([]);
  const [selectedOpenStock, setSelectedOpenStock] = useState([]);
  const [selectedSsd, setSelectedSsd] = useState([]);
  const [selectedStockCustomers, setSelectedStockCustomers] = useState([]);

  // Format currency
  const formatCurrency = (value) => {
    if (value === null || value === undefined || value === 0) return '$0';
    const num = Number(value) || 0;
    if (Math.abs(num) >= 1000000) {
      return `$${(num / 1000000).toFixed(2)}M`;
    }
    if (Math.abs(num) >= 1000) {
      return `$${(num / 1000).toFixed(1)}K`;
    }
    return `$${num.toFixed(0)}`;
  };

  // Format percentage
  const formatPct = (value) => {
    if (value === null || value === undefined) return '-';
    return `${(Number(value) * 100).toFixed(1)}%`;
  };

  // Fetch all data using getProductionSalesData (same as Target dashboard)
  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const functions = getFunctions();
      const getProductionSalesData = httpsCallable(functions, 'getProductionSalesData', { timeout: 150000 });

      // Build base filter params - locked to Division 60 (Anne Cole)
      const baseParams = {
        seasons: selectedSeason ? [selectedSeason] : null,
        divisions: ['60'],
        excludeLabels: excludedLabels.length > 0 ? excludedLabels : null,
        // Cross-filter selections
        filterCustomers: [...selectedPresold, ...selectedOpenStock].length > 0 ? [...selectedPresold, ...selectedOpenStock] : null,
      };

      // Fetch data: All customers for Division 60, then filter on frontend
      // Also fetch STOCK by invoiced customer (who STOCK was sold to)
      const [customerResult, stockByCustomerResult, ssdResult] = await Promise.all([
        getProductionSalesData({
          ...baseParams,
          groupBy: 'Customer'
        }),
        // Get STOCK production broken down by who it was invoiced to
        getProductionSalesData({
          ...baseParams,
          customers: ['STOCK'],
          groupBy: 'SalesCustomer'
        }),
        // For SSD table, fetch data filtered by SSD label
        getProductionSalesData({
          ...baseParams,
          groupBy: 'Customer',
          labels: ['SSD']
        }),
      ]);

      // Split customer data into Presold (non-STOCK) and Open Stock (STOCK)
      const allCustomerData = customerResult.data.summary || [];
      const presold = allCustomerData.filter(row => row.Customer !== 'STOCK');
      const openStock = allCustomerData.filter(row => row.Customer === 'STOCK');

      setPresoldData(presold);
      setOpenStockData(openStock);
      setSsdData(ssdResult.data.summary || []);
      setStockByCustomerData(stockByCustomerResult.data.summary || []);

      // Use totals from the full customer result (includes both Presold and STOCK)
      setTotals(customerResult.data.totals || {});
      setFilters(customerResult.data.filters || { seasons: [], customers: [], labels: [], divisions: [] });

    } catch (err) {
      console.error('Error fetching Anne Cole Cut and Sold data:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [selectedSeason, excludedLabels, selectedPresold, selectedOpenStock, selectedStockCustomers]);

  // Load data on mount and when filters change
  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Toggle label exclusion (clicking excludes/includes)
  const toggleLabelExclusion = (label) => {
    setExcludedLabels(prev =>
      prev.includes(label)
        ? prev.filter(l => l !== label)
        : [...prev, label]
    );
  };

  // Toggle cross-filter selections
  const togglePresold = (cust) => {
    setSelectedPresold(prev =>
      prev.includes(cust) ? prev.filter(c => c !== cust) : [...prev, cust]
    );
  };

  const toggleOpenStock = (cust) => {
    setSelectedOpenStock(prev =>
      prev.includes(cust) ? prev.filter(c => c !== cust) : [...prev, cust]
    );
  };

  const toggleSsd = (cust) => {
    setSelectedSsd(prev =>
      prev.includes(cust) ? prev.filter(c => c !== cust) : [...prev, cust]
    );
  };

  const toggleStockCustomer = (cust) => {
    setSelectedStockCustomers(prev =>
      prev.includes(cust) ? prev.filter(c => c !== cust) : [...prev, cust]
    );
  };

  // Clear filters
  const clearFilters = () => {
    setSelectedSeason('SP26');
    setExcludedLabels([]);
    setSelectedPresold([]);
    setSelectedOpenStock([]);
    setSelectedSsd([]);
    setSelectedStockCustomers([]);
  };

  // Clear only cross-filter selections
  const clearCrossFilters = () => {
    setSelectedPresold([]);
    setSelectedOpenStock([]);
    setSelectedSsd([]);
    setSelectedStockCustomers([]);
  };

  const hasCrossFilters = selectedPresold.length > 0 || selectedOpenStock.length > 0 || selectedSsd.length > 0 || selectedStockCustomers.length > 0;
  const hasActiveFilters = selectedSeason !== 'SP26' || excludedLabels.length > 0 || hasCrossFilters;

  // Get available labels for filter
  const availableLabels = filters.labels || [];
  const allLabels = [...availableLabels, '(No Label)'];

  // Export to Excel
  const handleExportExcel = () => {
    const wb = XLSX.utils.book_new();

    const mapRow = (row, groupField) => {
      const total = Number(row.Total_Production_Revenue) || 0;
      const totalCost = Number(row.Total_Production_Cost) || 0;
      const invoiced = Number(row.Invoiced_Revenue) || 0;
      const invoicedCost = Number(row.Invoiced_Cost) || 0;
      const pick = Number(row.On_Pick_Revenue) || 0;
      const pickCost = Number(row.On_Pick_Cost) || 0;
      const open = Number(row.Open_Confirmed_Revenue) || 0;
      const openCost = Number(row.Open_Confirmed_Cost) || 0;
      const leftToShip = total - invoiced;
      const leftToShipCost = totalCost - invoicedCost;
      const missing = leftToShip - pick - open;
      const missingCost = leftToShipCost - pickCost - openCost;

      return {
        [groupField]: row[groupField],
        'Total Production $': total,
        'Total GP%': total > 0 ? (total - totalCost) / total : 0,
        'Invoiced $': invoiced,
        'Invoiced GP%': invoiced > 0 ? (invoiced - invoicedCost) / invoiced : 0,
        'Left to Sell $': leftToShip,
        'Left to Sell GP%': leftToShip > 0 ? (leftToShip - leftToShipCost) / leftToShip : 0,
        'Pick $': pick,
        'Pick GP%': pick > 0 ? (pick - pickCost) / pick : 0,
        'Open $': open,
        'Open GP%': open > 0 ? (open - openCost) / open : 0,
        'Missing $': missing,
        'Missing GP%': missing > 0 ? (missing - missingCost) / missing : 0,
      };
    };

    if (presoldData.length > 0) {
      XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(presoldData.map(r => mapRow(r, 'Customer'))), 'Presold');
    }
    if (openStockData.length > 0) {
      XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(openStockData.map(r => mapRow(r, 'Customer'))), 'Open Stock');
    }
    if (ssdData.length > 0) {
      const ssdMapped = ssdData.map(r => {
        const row = mapRow(r, 'Customer');
        row.Customer = row.Customer === 'STOCK' ? 'Swimsuit Direct' : row.Customer;
        return row;
      });
      XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(ssdMapped), 'Swimsuit Direct');
    }
    if (stockByCustomerData.length > 0) {
      XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(stockByCustomerData.map(r => mapRow(r, 'SalesCustomer'))), 'Stock By Customer');
    }

    XLSX.writeFile(wb, `AnneCole_Div60_CutAndSold_${selectedSeason}_${new Date().toISOString().split('T')[0]}.xlsx`);
  };

  // Export to PDF - Champion style with summary cards
  const handleExportPDF = () => {
    const doc = new jsPDF('landscape');
    const pageWidth = doc.internal.pageSize.getWidth();

    // Anne Cole teal color
    const brandColor = [0, 128, 128];

    // Header
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(16);
    doc.setTextColor(0, 128, 128);
    doc.text('ANNE COLE', 14, 14);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(12);
    doc.setTextColor(50, 50, 50);
    doc.text(`Cut & Sold - Division 60 ${selectedSeason}`, 60, 14);
    doc.setFontSize(8);
    doc.setTextColor(150, 150, 150);
    doc.text(`Generated: ${new Date().toLocaleString()}`, 14, 21);

    // Summary Cards Row
    const cardY = 26;
    const cardH = 16;
    const cardGap = 3;
    const cardW = (pageWidth - 28 - cardGap * 5) / 6;
    const cards = [
      { label: 'Total Production', value: formatCurrency(summary.total), gp: formatPct(summary.totalGP), bg: [224, 242, 241] },
      { label: 'Invoiced', value: formatCurrency(summary.invoiced), gp: formatPct(summary.invoicedGP), bg: [220, 252, 231] },
      { label: 'Left to Sell', value: formatCurrency(summary.leftToShip), gp: formatPct(summary.leftToShipGP), bg: [243, 232, 255] },
      { label: 'Pick', value: formatCurrency(summary.pick), gp: formatPct(summary.pickGP), bg: [255, 237, 213] },
      { label: 'Open', value: formatCurrency(summary.open), gp: formatPct(summary.openGP), bg: [207, 250, 254] },
      { label: 'Missing', value: formatCurrency(summary.missing), gp: '-', bg: [241, 245, 249] },
    ];

    cards.forEach((card, i) => {
      const x = 14 + i * (cardW + cardGap);
      doc.setFillColor(...card.bg);
      doc.roundedRect(x, cardY, cardW, cardH, 2, 2, 'F');
      doc.setFontSize(6);
      doc.setTextColor(80, 80, 80);
      doc.text(card.label, x + cardW / 2, cardY + 4, { align: 'center' });
      doc.setFontSize(11);
      doc.setTextColor(30, 30, 30);
      doc.text(card.value, x + cardW / 2, cardY + 10, { align: 'center' });
      doc.setFontSize(6);
      doc.setTextColor(100, 100, 100);
      doc.text(`${card.gp} GP`, x + cardW / 2, cardY + 14, { align: 'center' });
    });

    const headers = [['', 'Total $', 'GP%', 'Invoiced $', 'GP%', 'Left to Sell $', 'GP%', 'Pick $', 'GP%', 'Open $', 'GP%', 'Missing $', 'GP%']];
    // Headers without Total $ and GP% for Open Stock and Swimsuit Direct (but with Landed Cost)
    const costOnlyHeaders = [['', 'Cost $', 'Invoiced $', 'GP%', 'Left to Sell $', 'GP%', 'Pick $', 'GP%', 'Open $', 'GP%', 'Missing $', 'GP%']];

    const mapPdfRow = (row, field) => {
      const total = Number(row.Total_Production_Revenue) || 0;
      const totalCost = Number(row.Total_Production_Cost) || 0;
      const invoiced = Number(row.Invoiced_Revenue) || 0;
      const invoicedCost = Number(row.Invoiced_Cost) || 0;
      const pick = Number(row.On_Pick_Revenue) || 0;
      const pickCost = Number(row.On_Pick_Cost) || 0;
      const open = Number(row.Open_Confirmed_Revenue) || 0;
      const openCost = Number(row.Open_Confirmed_Cost) || 0;
      const leftToShip = total - invoiced;
      const leftToShipCost = totalCost - invoicedCost;
      const missing = leftToShip - pick - open;
      const missingCost = leftToShipCost - pickCost - openCost;

      return [
        row[field] || '-',
        formatCurrency(total),
        formatPct(total > 0 ? (total - totalCost) / total : 0),
        formatCurrency(invoiced),
        formatPct(invoiced > 0 ? (invoiced - invoicedCost) / invoiced : 0),
        formatCurrency(leftToShip),
        formatPct(leftToShip > 0 ? (leftToShip - leftToShipCost) / leftToShip : 0),
        formatCurrency(pick),
        formatPct(pick > 0 ? (pick - pickCost) / pick : 0),
        formatCurrency(open),
        formatPct(open > 0 ? (open - openCost) / open : 0),
        formatCurrency(missing),
        formatPct(missing > 0 ? (missing - missingCost) / missing : 0),
      ];
    };

    // Cost-only row mapper (without Total $ and GP%, but with Landed Cost) for Open Stock and Swimsuit Direct
    const mapPdfRowCostOnly = (row, field) => {
      const total = Number(row.Total_Production_Revenue) || 0;
      const totalCost = Number(row.Total_Production_Cost) || 0;
      const invoiced = Number(row.Invoiced_Revenue) || 0;
      const invoicedCost = Number(row.Invoiced_Cost) || 0;
      const pick = Number(row.On_Pick_Revenue) || 0;
      const pickCost = Number(row.On_Pick_Cost) || 0;
      const open = Number(row.Open_Confirmed_Revenue) || 0;
      const openCost = Number(row.Open_Confirmed_Cost) || 0;
      const leftToShip = total - invoiced;
      const leftToShipCost = totalCost - invoicedCost;
      const missing = leftToShip - pick - open;
      const missingCost = leftToShipCost - pickCost - openCost;

      return [
        row[field] || '-',
        formatCurrency(totalCost),
        formatCurrency(invoiced),
        formatPct(invoiced > 0 ? (invoiced - invoicedCost) / invoiced : 0),
        formatCurrency(leftToShip),
        formatPct(leftToShip > 0 ? (leftToShip - leftToShipCost) / leftToShip : 0),
        formatCurrency(pick),
        formatPct(pick > 0 ? (pick - pickCost) / pick : 0),
        formatCurrency(open),
        formatPct(open > 0 ? (open - openCost) / open : 0),
        formatCurrency(missing),
        formatPct(missing > 0 ? (missing - missingCost) / missing : 0),
      ];
    };

    // Calculate totals for a dataset
    const calcTableTotals = (data) => {
      const t = data.reduce((acc, row) => {
        acc.total += Number(row.Total_Production_Revenue) || 0;
        acc.totalCost += Number(row.Total_Production_Cost) || 0;
        acc.invoiced += Number(row.Invoiced_Revenue) || 0;
        acc.invoicedCost += Number(row.Invoiced_Cost) || 0;
        acc.pick += Number(row.On_Pick_Revenue) || 0;
        acc.pickCost += Number(row.On_Pick_Cost) || 0;
        acc.open += Number(row.Open_Confirmed_Revenue) || 0;
        acc.openCost += Number(row.Open_Confirmed_Cost) || 0;
        return acc;
      }, { total: 0, totalCost: 0, invoiced: 0, invoicedCost: 0, pick: 0, pickCost: 0, open: 0, openCost: 0 });

      const leftToShip = t.total - t.invoiced;
      const leftToShipCost = t.totalCost - t.invoicedCost;
      const missing = leftToShip - t.pick - t.open;
      const missingCost = leftToShipCost - t.pickCost - t.openCost;

      return [
        'TOTAL',
        formatCurrency(t.total),
        formatPct(t.total > 0 ? (t.total - t.totalCost) / t.total : 0),
        formatCurrency(t.invoiced),
        formatPct(t.invoiced > 0 ? (t.invoiced - t.invoicedCost) / t.invoiced : 0),
        formatCurrency(leftToShip),
        formatPct(leftToShip > 0 ? (leftToShip - leftToShipCost) / leftToShip : 0),
        formatCurrency(t.pick),
        formatPct(t.pick > 0 ? (t.pick - t.pickCost) / t.pick : 0),
        formatCurrency(t.open),
        formatPct(t.open > 0 ? (t.open - t.openCost) / t.open : 0),
        formatCurrency(missing),
        formatPct(missing > 0 ? (missing - missingCost) / missing : 0),
      ];
    };

    // Cost-only totals (without Total $ and GP%, but with Landed Cost) for Open Stock and Swimsuit Direct
    const calcTableTotalsCostOnly = (data) => {
      const t = data.reduce((acc, row) => {
        acc.total += Number(row.Total_Production_Revenue) || 0;
        acc.totalCost += Number(row.Total_Production_Cost) || 0;
        acc.invoiced += Number(row.Invoiced_Revenue) || 0;
        acc.invoicedCost += Number(row.Invoiced_Cost) || 0;
        acc.pick += Number(row.On_Pick_Revenue) || 0;
        acc.pickCost += Number(row.On_Pick_Cost) || 0;
        acc.open += Number(row.Open_Confirmed_Revenue) || 0;
        acc.openCost += Number(row.Open_Confirmed_Cost) || 0;
        return acc;
      }, { total: 0, totalCost: 0, invoiced: 0, invoicedCost: 0, pick: 0, pickCost: 0, open: 0, openCost: 0 });

      const leftToShip = t.total - t.invoiced;
      const leftToShipCost = t.totalCost - t.invoicedCost;
      const missing = leftToShip - t.pick - t.open;
      const missingCost = leftToShipCost - t.pickCost - t.openCost;

      return [
        'TOTAL',
        formatCurrency(t.totalCost),
        formatCurrency(t.invoiced),
        formatPct(t.invoiced > 0 ? (t.invoiced - t.invoicedCost) / t.invoiced : 0),
        formatCurrency(leftToShip),
        formatPct(leftToShip > 0 ? (leftToShip - leftToShipCost) / leftToShip : 0),
        formatCurrency(t.pick),
        formatPct(t.pick > 0 ? (t.pick - t.pickCost) / t.pick : 0),
        formatCurrency(t.open),
        formatPct(t.open > 0 ? (t.open - t.openCost) / t.open : 0),
        formatCurrency(missing),
        formatPct(missing > 0 ? (missing - missingCost) / missing : 0),
      ];
    };

    let yPos = 48;

    // Presold table
    if (presoldData.length > 0) {
      doc.setFontSize(10);
      doc.setTextColor(50, 50, 50);
      doc.text('Presold', 14, yPos);
      const presoldRows = presoldData.slice(0, 15).map(r => mapPdfRow(r, 'Customer'));
      presoldRows.push(calcTableTotals(presoldData.slice(0, 15)));
      autoTable(doc, {
        startY: yPos + 3,
        head: headers,
        body: presoldRows,
        theme: 'grid',
        styles: { fontSize: 7 },
        headStyles: { fillColor: brandColor },
        didParseCell: (data) => {
          // Style the totals row (last row)
          if (data.row.index === presoldRows.length - 1) {
            data.cell.styles.fontStyle = 'bold';
            data.cell.styles.fillColor = [241, 245, 249];
          }
        }
      });
      yPos = doc.lastAutoTable.finalY + 6;
    }

    // Swimsuit Direct Summary - Pivoted Table (3 rows: Price, Cost, GP%)
    if (ssdData.length > 0) {
      doc.setFontSize(10);
      doc.text('Swimsuit Direct Summary', 14, yPos);

      // Calculate totals for Swimsuit Direct
      const ssdTotals = ssdData.reduce((acc, row) => {
        acc.total += Number(row.Total_Production_Revenue) || 0;
        acc.totalCost += Number(row.Total_Production_Cost) || 0;
        acc.invoiced += Number(row.Invoiced_Revenue) || 0;
        acc.invoicedCost += Number(row.Invoiced_Cost) || 0;
        acc.pick += Number(row.On_Pick_Revenue) || 0;
        acc.pickCost += Number(row.On_Pick_Cost) || 0;
        acc.open += Number(row.Open_Confirmed_Revenue) || 0;
        acc.openCost += Number(row.Open_Confirmed_Cost) || 0;
        return acc;
      }, { total: 0, totalCost: 0, invoiced: 0, invoicedCost: 0, pick: 0, pickCost: 0, open: 0, openCost: 0 });

      const calcGPSsd = (rev, cost) => rev > 0 ? (rev - cost) / rev : 0;

      const pivotHeadersSsd = [['', 'Total Prod', 'Invoiced', 'Pick', 'Open']];
      const ssdPivotRows = [
        ['Cost', formatCurrency(ssdTotals.totalCost), formatCurrency(ssdTotals.invoicedCost), formatCurrency(ssdTotals.pickCost), formatCurrency(ssdTotals.openCost)],
        ['GP%', formatPct(calcGPSsd(ssdTotals.total, ssdTotals.totalCost)), formatPct(calcGPSsd(ssdTotals.invoiced, ssdTotals.invoicedCost)), formatPct(calcGPSsd(ssdTotals.pick, ssdTotals.pickCost)), formatPct(calcGPSsd(ssdTotals.open, ssdTotals.openCost))]
      ];

      autoTable(doc, {
        startY: yPos + 3,
        head: pivotHeadersSsd,
        body: ssdPivotRows,
        theme: 'grid',
        styles: { fontSize: 7 },
        headStyles: { fillColor: [225, 29, 72] }, // Rose color
        didParseCell: (data) => {
          if (data.row.index === 0) { // Cost row
            data.cell.styles.fillColor = [255, 241, 242];
          }
        }
      });
      yPos = doc.lastAutoTable.finalY + 6;
    }

    // Open Stock Summary - Pivoted Table (3 rows: Price, Cost, GP%)
    if (openStockData.length > 0) {
      doc.setFontSize(10);
      doc.text('Open Stock Summary', 14, yPos);

      // Calculate totals for Open Stock
      const stockTotals = openStockData.reduce((acc, row) => {
        acc.total += Number(row.Total_Production_Revenue) || 0;
        acc.totalCost += Number(row.Total_Production_Cost) || 0;
        acc.invoiced += Number(row.Invoiced_Revenue) || 0;
        acc.invoicedCost += Number(row.Invoiced_Cost) || 0;
        acc.pick += Number(row.On_Pick_Revenue) || 0;
        acc.pickCost += Number(row.On_Pick_Cost) || 0;
        acc.open += Number(row.Open_Confirmed_Revenue) || 0;
        acc.openCost += Number(row.Open_Confirmed_Cost) || 0;
        return acc;
      }, { total: 0, totalCost: 0, invoiced: 0, invoicedCost: 0, pick: 0, pickCost: 0, open: 0, openCost: 0 });

      const calcGP = (rev, cost) => rev > 0 ? (rev - cost) / rev : 0;

      const pivotHeaders = [['', 'Total Prod', 'Invoiced', 'Pick', 'Open']];
      const stockPivotRows = [
        ['Cost', formatCurrency(stockTotals.totalCost), formatCurrency(stockTotals.invoicedCost), formatCurrency(stockTotals.pickCost), formatCurrency(stockTotals.openCost)],
        ['GP%', formatPct(calcGP(stockTotals.total, stockTotals.totalCost)), formatPct(calcGP(stockTotals.invoiced, stockTotals.invoicedCost)), formatPct(calcGP(stockTotals.pick, stockTotals.pickCost)), formatPct(calcGP(stockTotals.open, stockTotals.openCost))]
      ];

      autoTable(doc, {
        startY: yPos + 3,
        head: pivotHeaders,
        body: stockPivotRows,
        theme: 'grid',
        styles: { fontSize: 7 },
        headStyles: { fillColor: [0, 150, 150] }, // Cyan color
        didParseCell: (data) => {
          if (data.row.index === 0) { // Cost row
            data.cell.styles.fillColor = [224, 242, 241];
          }
        }
      });
    }

    // Page 2: Stock By Customer (who STOCK was invoiced to) - Sales Only columns
    if (stockByCustomerData.length > 0) {
      doc.addPage();
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(16);
      doc.setTextColor(0, 128, 128);
      doc.text('ANNE COLE', 14, 14);
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(12);
      doc.setTextColor(50, 50, 50);
      doc.text(`Cut & Sold - Division 60 ${selectedSeason}`, 60, 14);
      doc.setFontSize(8);
      doc.setTextColor(150, 150, 150);
      doc.text(`Generated: ${new Date().toLocaleString()}`, 14, 21);

      doc.setFontSize(10);
      doc.setTextColor(50, 50, 50);
      doc.text('Stock By Customer (Invoiced To)', 14, 30);

      // Sales-only headers (Invoice, Pick, Open only)
      const salesOnlyHeaders = [['Customer', 'Invoiced $', 'GP%', 'Pick $', 'GP%', 'Open $', 'GP%']];

      // Sales-only row mapper
      const mapSalesOnlyRow = (row) => {
        const invoiced = Number(row.Invoiced_Revenue) || 0;
        const invoicedCost = Number(row.Invoiced_Cost) || 0;
        const pick = Number(row.On_Pick_Revenue) || 0;
        const pickCost = Number(row.On_Pick_Cost) || 0;
        const open = Number(row.Open_Confirmed_Revenue) || 0;
        const openCost = Number(row.Open_Confirmed_Cost) || 0;
        return [
          row.SalesCustomer || '-',
          formatCurrency(invoiced),
          formatPct(invoiced > 0 ? (invoiced - invoicedCost) / invoiced : 0),
          formatCurrency(pick),
          formatPct(pick > 0 ? (pick - pickCost) / pick : 0),
          formatCurrency(open),
          formatPct(open > 0 ? (open - openCost) / open : 0),
        ];
      };

      // Sales-only totals
      const calcSalesOnlyTotals = (data) => {
        const t = data.reduce((acc, row) => {
          acc.invoiced += Number(row.Invoiced_Revenue) || 0;
          acc.invoicedCost += Number(row.Invoiced_Cost) || 0;
          acc.pick += Number(row.On_Pick_Revenue) || 0;
          acc.pickCost += Number(row.On_Pick_Cost) || 0;
          acc.open += Number(row.Open_Confirmed_Revenue) || 0;
          acc.openCost += Number(row.Open_Confirmed_Cost) || 0;
          return acc;
        }, { invoiced: 0, invoicedCost: 0, pick: 0, pickCost: 0, open: 0, openCost: 0 });
        return [
          'TOTAL',
          formatCurrency(t.invoiced),
          formatPct(t.invoiced > 0 ? (t.invoiced - t.invoicedCost) / t.invoiced : 0),
          formatCurrency(t.pick),
          formatPct(t.pick > 0 ? (t.pick - t.pickCost) / t.pick : 0),
          formatCurrency(t.open),
          formatPct(t.open > 0 ? (t.open - t.openCost) / t.open : 0),
        ];
      };

      const stockCustRows = stockByCustomerData.slice(0, 30).map(mapSalesOnlyRow);
      stockCustRows.push(calcSalesOnlyTotals(stockByCustomerData.slice(0, 30)));
      autoTable(doc, {
        startY: 33,
        head: salesOnlyHeaders,
        body: stockCustRows,
        theme: 'grid',
        styles: { fontSize: 7 },
        headStyles: { fillColor: [59, 130, 246] }, // Blue for Stock By Customer
        didParseCell: (data) => {
          if (data.row.index === stockCustRows.length - 1) {
            data.cell.styles.fontStyle = 'bold';
            data.cell.styles.fillColor = [241, 245, 249];
          }
        }
      });
    }

    doc.save(`AnneCole_Div60_CutAndSold_${selectedSeason}.pdf`);
  };

  // Data Table Component with cross-filter support
  // costOnly: if true, shows only landed cost (no sales columns)
  // salesOnly: if true, shows only Invoice, Pick, Open columns (for Stock By Customer)
  const DataTable = ({ title, data, groupByField, color = 'slate', selectedItems = [], onItemClick, costOnly = false, salesOnly = false }) => {
    // Calculate totals for footer
    const calcTotals = data.reduce((acc, row) => {
      const total = Number(row.Total_Production_Revenue) || 0;
      const totalCost = Number(row.Total_Production_Cost) || 0;
      const invoiced = Number(row.Invoiced_Revenue) || 0;
      const invoicedCost = Number(row.Invoiced_Cost) || 0;
      const pick = Number(row.On_Pick_Revenue) || 0;
      const pickCost = Number(row.On_Pick_Cost) || 0;
      const open = Number(row.Open_Confirmed_Revenue) || 0;
      const openCost = Number(row.Open_Confirmed_Cost) || 0;
      const leftToShip = total - invoiced;
      const leftToShipCost = totalCost - invoicedCost;
      const missing = leftToShip - pick - open;
      const missingCost = leftToShipCost - pickCost - openCost;

      return {
        total: acc.total + total,
        totalCost: acc.totalCost + totalCost,
        invoiced: acc.invoiced + invoiced,
        invoicedCost: acc.invoicedCost + invoicedCost,
        leftToShip: acc.leftToShip + leftToShip,
        leftToShipCost: acc.leftToShipCost + leftToShipCost,
        pick: acc.pick + pick,
        pickCost: acc.pickCost + pickCost,
        open: acc.open + open,
        openCost: acc.openCost + openCost,
        missing: acc.missing + missing,
        missingCost: acc.missingCost + missingCost,
      };
    }, { total: 0, totalCost: 0, invoiced: 0, invoicedCost: 0, leftToShip: 0, leftToShipCost: 0, pick: 0, pickCost: 0, open: 0, openCost: 0, missing: 0, missingCost: 0 });

    // Calculate GP% for totals
    const totalGP = calcTotals.total > 0 ? (calcTotals.total - calcTotals.totalCost) / calcTotals.total : 0;
    const invoicedGP = calcTotals.invoiced > 0 ? (calcTotals.invoiced - calcTotals.invoicedCost) / calcTotals.invoiced : 0;
    const leftToShipGP = calcTotals.leftToShip > 0 ? (calcTotals.leftToShip - calcTotals.leftToShipCost) / calcTotals.leftToShip : 0;
    const pickGP = calcTotals.pick > 0 ? (calcTotals.pick - calcTotals.pickCost) / calcTotals.pick : 0;
    const openGP = calcTotals.open > 0 ? (calcTotals.open - calcTotals.openCost) / calcTotals.open : 0;
    const missingGP = calcTotals.missing > 0 ? (calcTotals.missing - calcTotals.missingCost) / calcTotals.missing : 0;

    // Helper to calculate row-level values and GP%
    const getRowData = (row) => {
      const total = Number(row.Total_Production_Revenue) || 0;
      const totalCost = Number(row.Total_Production_Cost) || 0;
      const invoiced = Number(row.Invoiced_Revenue) || 0;
      const invoicedCost = Number(row.Invoiced_Cost) || 0;
      const pick = Number(row.On_Pick_Revenue) || 0;
      const pickCost = Number(row.On_Pick_Cost) || 0;
      const open = Number(row.Open_Confirmed_Revenue) || 0;
      const openCost = Number(row.Open_Confirmed_Cost) || 0;
      const leftToShip = total - invoiced;
      const leftToShipCost = totalCost - invoicedCost;
      const missing = leftToShip - pick - open;
      const missingCost = leftToShipCost - pickCost - openCost;

      return {
        total,
        totalCost,
        totalGP: total > 0 ? (total - totalCost) / total : 0,
        invoiced,
        invoicedCost,
        invoicedGP: invoiced > 0 ? (invoiced - invoicedCost) / invoiced : 0,
        leftToShip,
        leftToShipCost,
        leftToShipGP: leftToShip > 0 ? (leftToShip - leftToShipCost) / leftToShip : 0,
        pick,
        pickCost,
        pickGP: pick > 0 ? (pick - pickCost) / pick : 0,
        open,
        openCost,
        openGP: open > 0 ? (open - openCost) / open : 0,
        missing,
        missingGP: missing > 0 ? (missing - missingCost) / missing : 0,
      };
    };

    const gpColor = (gp) => gp >= 0.3 ? 'text-green-600' : gp < 0 ? 'text-red-600' : 'text-slate-500';
    const hasSelection = selectedItems.length > 0;

    return (
      <div className={`bg-white rounded-lg border overflow-hidden shadow-sm mb-4 ${hasSelection ? 'border-teal-300 ring-1 ring-teal-200' : 'border-slate-200'}`}>
        {/* Header */}
        <div className={`px-4 py-2.5 border-b border-slate-100 bg-gradient-to-r from-${color}-50 to-white flex items-center justify-between`}>
          <div className="flex items-center gap-2">
            <div className={`w-6 h-6 rounded-md bg-${color}-100 flex items-center justify-center`}>
            </div>
            <div>
              <h3 className="text-xs font-semibold text-slate-800">{title}</h3>
              <p className="text-[10px] text-slate-400">
                {data.length} rows {hasSelection && <span className="text-teal-600 font-medium">• {selectedItems.length} selected</span>}
              </p>
            </div>
          </div>
          <span className="text-[10px] text-slate-400 italic">Click row to filter</span>
        </div>

        {/* Table */}
        <div className="overflow-x-auto" style={{ maxHeight: '280px' }}>
          <table className="w-full text-xs">
            <thead className="sticky top-0 bg-slate-50 z-10">
              <tr>
                <th className="px-3 py-2 text-left font-semibold text-slate-600 border-b border-slate-200 whitespace-nowrap">{groupByField.replace('_', ' ')}</th>
                {!salesOnly && (
                  <th className="px-2 py-2 text-right font-semibold text-teal-500 border-b border-slate-200 bg-teal-50/30">Cost <span className="text-[9px] font-normal opacity-60">(Landed)</span></th>
                )}
                {!costOnly && !salesOnly && (
                  <>
                    <th className="px-2 py-2 text-right font-semibold text-teal-600 border-b border-slate-200 bg-teal-50/50">Total $</th>
                    <th className="px-2 py-2 text-right font-semibold text-teal-600 border-b border-slate-200 bg-teal-50/50">GP%</th>
                  </>
                )}
                <th className="px-2 py-2 text-right font-semibold text-green-600 border-b border-slate-200 bg-green-50/50">{salesOnly ? 'Invoice Cost' : 'Invoice $'}</th>
                <th className="px-2 py-2 text-right font-semibold text-green-600 border-b border-slate-200 bg-green-50/50">GP%</th>
                {!salesOnly && (
                  <>
                    <th className="px-2 py-2 text-right font-semibold text-purple-500 border-b border-slate-200 bg-purple-50/30">LTS <span className="text-[9px] font-normal opacity-60">(Landed)</span></th>
                    <th className="px-2 py-2 text-right font-semibold text-purple-600 border-b border-slate-200 bg-purple-50/50">Left to Sell $</th>
                    <th className="px-2 py-2 text-right font-semibold text-purple-600 border-b border-slate-200 bg-purple-50/50">GP%</th>
                  </>
                )}
                <th className="px-2 py-2 text-right font-semibold text-orange-600 border-b border-slate-200 bg-orange-50/50">{salesOnly ? 'Pick Cost' : 'Pick $'}</th>
                <th className="px-2 py-2 text-right font-semibold text-orange-600 border-b border-slate-200 bg-orange-50/50">GP%</th>
                <th className="px-2 py-2 text-right font-semibold text-cyan-600 border-b border-slate-200 bg-cyan-50/50">{salesOnly ? 'Open Cost' : 'Open $'}</th>
                <th className="px-2 py-2 text-right font-semibold text-cyan-600 border-b border-slate-200 bg-cyan-50/50">GP%</th>
                {!salesOnly && (
                  <>
                    <th className="px-2 py-2 text-right font-semibold text-slate-600 border-b border-slate-200 bg-slate-50/50">Missing $</th>
                    <th className="px-2 py-2 text-right font-semibold text-slate-600 border-b border-slate-200 bg-slate-50/50">GP%</th>
                  </>
                )}
              </tr>
            </thead>
            <tbody>
              {data.map((row, idx) => {
                const rowData = getRowData(row);
                const itemValue = row[groupByField];
                const isSelected = selectedItems.includes(itemValue);
                return (
                  <tr
                    key={idx}
                    onClick={() => onItemClick && onItemClick(itemValue)}
                    className={`border-b border-slate-100 transition-colors cursor-pointer ${
                      isSelected
                        ? 'bg-teal-100 hover:bg-teal-150 border-l-4 border-l-teal-500'
                        : 'hover:bg-slate-50'
                    }`}
                  >
                    <td className={`px-3 py-2 font-medium truncate max-w-[160px] ${isSelected ? 'text-teal-700' : 'text-slate-700'}`}>
                      {itemValue || '-'}
                    </td>
                    {!salesOnly && (
                      <td className="px-2 py-2 text-right tabular-nums text-teal-500">{formatCurrency(rowData.totalCost)}</td>
                    )}
                    {!costOnly && !salesOnly && (
                      <>
                        <td className="px-2 py-2 text-right tabular-nums font-medium text-slate-800">{formatCurrency(rowData.total)}</td>
                        <td className={`px-2 py-2 text-right tabular-nums ${gpColor(rowData.totalGP)}`}>{formatPct(rowData.totalGP)}</td>
                      </>
                    )}
                    <td className="px-2 py-2 text-right tabular-nums text-slate-600">{formatCurrency(salesOnly ? rowData.invoicedCost : rowData.invoiced)}</td>
                    <td className={`px-2 py-2 text-right tabular-nums ${gpColor(rowData.invoicedGP)}`}>{formatPct(rowData.invoicedGP)}</td>
                    {!salesOnly && (
                      <>
                        <td className="px-2 py-2 text-right tabular-nums text-purple-500">{formatCurrency(rowData.leftToShipCost)}</td>
                        <td className="px-2 py-2 text-right tabular-nums text-slate-600">{formatCurrency(rowData.leftToShip)}</td>
                        <td className={`px-2 py-2 text-right tabular-nums ${gpColor(rowData.leftToShipGP)}`}>{formatPct(rowData.leftToShipGP)}</td>
                      </>
                    )}
                    <td className="px-2 py-2 text-right tabular-nums text-slate-600">{formatCurrency(salesOnly ? rowData.pickCost : rowData.pick)}</td>
                    <td className={`px-2 py-2 text-right tabular-nums ${gpColor(rowData.pickGP)}`}>{formatPct(rowData.pickGP)}</td>
                    <td className="px-2 py-2 text-right tabular-nums text-slate-600">{formatCurrency(salesOnly ? rowData.openCost : rowData.open)}</td>
                    <td className={`px-2 py-2 text-right tabular-nums ${gpColor(rowData.openGP)}`}>{formatPct(rowData.openGP)}</td>
                    {!salesOnly && (
                      <>
                        <td className={`px-2 py-2 text-right tabular-nums font-medium ${rowData.missing > 0 ? 'text-red-600' : 'text-slate-400'}`}>{formatCurrency(rowData.missing)}</td>
                        <td className={`px-2 py-2 text-right tabular-nums ${gpColor(rowData.missingGP)}`}>{formatPct(rowData.missingGP)}</td>
                      </>
                    )}
                  </tr>
                );
              })}
            </tbody>
            {data.length > 0 && (
              <tfoot className="sticky bottom-0 bg-slate-100 border-t-2 border-slate-300">
                <tr className="font-semibold text-slate-700">
                  <td className="px-3 py-2">TOTAL</td>
                  {!salesOnly && (
                    <td className="px-2 py-2 text-right tabular-nums font-bold text-teal-500">{formatCurrency(calcTotals.totalCost)}</td>
                  )}
                  {!costOnly && !salesOnly && (
                    <>
                      <td className="px-2 py-2 text-right tabular-nums font-bold text-slate-800">{formatCurrency(calcTotals.total)}</td>
                      <td className={`px-2 py-2 text-right tabular-nums ${gpColor(totalGP)}`}>{formatPct(totalGP)}</td>
                    </>
                  )}
                  <td className="px-2 py-2 text-right tabular-nums">{formatCurrency(salesOnly ? calcTotals.invoicedCost : calcTotals.invoiced)}</td>
                  <td className={`px-2 py-2 text-right tabular-nums ${gpColor(invoicedGP)}`}>{formatPct(invoicedGP)}</td>
                  {!salesOnly && (
                    <>
                      <td className="px-2 py-2 text-right tabular-nums font-bold text-purple-500">{formatCurrency(calcTotals.leftToShipCost)}</td>
                      <td className="px-2 py-2 text-right tabular-nums">{formatCurrency(calcTotals.leftToShip)}</td>
                      <td className={`px-2 py-2 text-right tabular-nums ${gpColor(leftToShipGP)}`}>{formatPct(leftToShipGP)}</td>
                    </>
                  )}
                  <td className="px-2 py-2 text-right tabular-nums">{formatCurrency(salesOnly ? calcTotals.pickCost : calcTotals.pick)}</td>
                  <td className={`px-2 py-2 text-right tabular-nums ${gpColor(pickGP)}`}>{formatPct(pickGP)}</td>
                  <td className="px-2 py-2 text-right tabular-nums">{formatCurrency(salesOnly ? calcTotals.openCost : calcTotals.open)}</td>
                  <td className={`px-2 py-2 text-right tabular-nums ${gpColor(openGP)}`}>{formatPct(openGP)}</td>
                  {!salesOnly && (
                    <>
                      <td className={`px-2 py-2 text-right tabular-nums font-bold ${calcTotals.missing > 0 ? 'text-red-600' : 'text-slate-400'}`}>{formatCurrency(calcTotals.missing)}</td>
                      <td className={`px-2 py-2 text-right tabular-nums ${gpColor(missingGP)}`}>{formatPct(missingGP)}</td>
                    </>
                  )}
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </div>
    );
  };

  // Calculate summary totals - only from Presold data (excludes STOCK)
  const calcSummary = () => {
    // Sum up from presoldData only
    const presoldTotals = presoldData.reduce((acc, row) => {
      acc.total += Number(row.Total_Production_Revenue) || 0;
      acc.totalCost += Number(row.Total_Production_Cost) || 0;
      acc.invoiced += Number(row.Invoiced_Revenue) || 0;
      acc.invoicedCost += Number(row.Invoiced_Cost) || 0;
      acc.pick += Number(row.On_Pick_Revenue) || 0;
      acc.pickCost += Number(row.On_Pick_Cost) || 0;
      acc.open += Number(row.Open_Confirmed_Revenue) || 0;
      acc.openCost += Number(row.Open_Confirmed_Cost) || 0;
      return acc;
    }, { total: 0, totalCost: 0, invoiced: 0, invoicedCost: 0, pick: 0, pickCost: 0, open: 0, openCost: 0 });

    const { total, totalCost, invoiced, invoicedCost, pick, pickCost, open, openCost } = presoldTotals;
    const leftToShip = total - invoiced;
    const leftToShipCost = totalCost - invoicedCost;
    const missing = leftToShip - pick - open;

    // Calculate Open Stock and SSD totals (cost only)
    const openStockCost = openStockData.reduce((sum, row) => sum + (Number(row.Total_Production_Cost) || 0), 0);
    const ssdCost = ssdData.reduce((sum, row) => sum + (Number(row.Total_Production_Cost) || 0), 0);

    return {
      total, totalCost, totalGP: total > 0 ? (total - totalCost) / total : 0,
      invoiced, invoicedGP: invoiced > 0 ? (invoiced - invoicedCost) / invoiced : 0,
      leftToShip, leftToShipCost, leftToShipGP: leftToShip > 0 ? (leftToShip - leftToShipCost) / leftToShip : 0,
      pick, pickGP: pick > 0 ? (pick - pickCost) / pick : 0,
      open, openGP: open > 0 ? (open - openCost) / open : 0,
      missing,
      openStockCost,
      ssdCost
    };
  };

  const summary = calcSummary();
  const gpColor = (gp) => gp >= 0.3 ? 'text-green-600' : gp < 0 ? 'text-red-600' : 'text-slate-600';

  return (
    <div className="space-y-4">
      {/* Header - Anne Cole branding with teal colors */}
      <div className="bg-teal-50 border border-teal-200 rounded-lg px-4 py-2 flex items-center justify-between">
        <div className="flex items-center gap-3">
          {/* Anne Cole Logo */}
          <div className="flex items-center">
            <span className="text-xl font-serif font-bold text-teal-700 tracking-wide">ANNE COLE</span>
          </div>
          <span className="text-sm font-medium text-teal-700">Division 60</span>
          <span className="text-xs text-teal-500">| {selectedSeason}</span>
        </div>
      </div>

      {/* Anne Cole Totals Summary */}
      {!loading && !error && (
        <div className="bg-white rounded-lg border border-slate-200 shadow-sm p-4">
          <h3 className="text-sm font-semibold text-slate-700 mb-3">Anne Cole Division 60 Summary <span className="text-xs font-normal text-slate-500">(Presold Only)</span></h3>
          <div className="grid grid-cols-6 gap-3">
            <div className="bg-teal-50 rounded-lg p-3 text-center">
              <p className="text-[10px] text-teal-600 font-medium">Total Production</p>
              <p className="text-lg font-bold text-teal-700">{formatCurrency(summary.total)}</p>
              <p className="text-[10px] text-teal-500">Cost: {formatCurrency(summary.totalCost)}</p>
              <p className={`text-xs ${gpColor(summary.totalGP)}`}>{formatPct(summary.totalGP)} GP</p>
            </div>
            <div className="bg-green-50 rounded-lg p-3 text-center">
              <p className="text-[10px] text-green-600 font-medium">Invoiced</p>
              <p className="text-lg font-bold text-green-700">{formatCurrency(summary.invoiced)}</p>
              <p className={`text-xs ${gpColor(summary.invoicedGP)}`}>{formatPct(summary.invoicedGP)} GP</p>
            </div>
            <div className="bg-purple-50 rounded-lg p-3 text-center">
              <p className="text-[10px] text-purple-600 font-medium">Left to Sell</p>
              <p className="text-lg font-bold text-purple-700">{formatCurrency(summary.leftToShip)}</p>
              <p className="text-[10px] text-purple-500">Cost: {formatCurrency(summary.leftToShipCost)}</p>
              <p className={`text-xs ${gpColor(summary.leftToShipGP)}`}>{formatPct(summary.leftToShipGP)} GP</p>
            </div>
            <div className="bg-slate-100 rounded-lg p-3 text-center">
              <p className="text-[10px] text-slate-600 font-medium">Missing</p>
              <p className="text-lg font-bold text-slate-700">{formatCurrency(summary.missing)}</p>
              <p className="text-xs text-slate-400">-</p>
            </div>
            <div className="bg-cyan-50 rounded-lg p-3 text-center border-2 border-cyan-200">
              <p className="text-[10px] text-cyan-600 font-medium">Open Stock Cost</p>
              <p className="text-lg font-bold text-cyan-700">{formatCurrency(summary.openStockCost)}</p>
              <p className="text-[10px] text-cyan-500">Landed Cost</p>
            </div>
            <div className="bg-rose-50 rounded-lg p-3 text-center border-2 border-rose-200">
              <p className="text-[10px] text-rose-600 font-medium">Swimsuit Direct Cost</p>
              <p className="text-lg font-bold text-rose-700">{formatCurrency(summary.ssdCost)}</p>
              <p className="text-[10px] text-rose-500">Landed Cost</p>
            </div>
          </div>
        </div>
      )}

      {/* Filters Card */}
      <div className="bg-white rounded-lg border border-slate-200 shadow-sm">
        <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between">
          <div className="flex items-center gap-4">
            {/* Season Filter */}
            <div className="flex items-center gap-1.5">
              <span className="text-xs font-medium text-slate-500">Season:</span>
              {['SP26', 'FA25', 'SP25'].map((s) => (
                <button
                  key={s}
                  onClick={() => setSelectedSeason(s)}
                  className={`px-2.5 py-1 text-xs font-medium rounded transition-colors ${
                    selectedSeason === s
                      ? 'bg-teal-600 text-white'
                      : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                  }`}
                >
                  {s}
                </button>
              ))}
            </div>
          </div>

          <div className="flex items-center gap-2">
            {hasActiveFilters && (
              <button onClick={clearFilters} className="px-2 py-1 text-xs text-teal-600 hover:bg-teal-50 rounded">
                Reset
              </button>
            )}
            <button onClick={handleExportExcel} className="flex items-center gap-1 px-2.5 py-1 text-xs bg-green-600 text-white rounded hover:bg-green-700">
              <Download size={12} /> Excel
            </button>
            <button onClick={handleExportPDF} className="flex items-center gap-1 px-2.5 py-1 text-xs bg-slate-600 text-white rounded hover:bg-slate-700">
              <FileText size={12} /> PDF
            </button>
            <button onClick={fetchData} disabled={loading} className="flex items-center gap-1 px-2.5 py-1 text-xs bg-teal-600 text-white rounded hover:bg-teal-700 disabled:opacity-50">
              <RefreshCw size={12} className={loading ? 'animate-spin' : ''} />
            </button>
          </div>
        </div>

        {/* Labels Filter - Exclude Mode */}
        <div className="px-4 py-2.5 bg-slate-50/50">
          <div className="flex items-center gap-2 flex-wrap">
            <div className="flex items-center gap-1.5">
              <Filter size={12} className="text-slate-400" />
              <span className="text-xs font-medium text-slate-500">Labels</span>
              <span className="text-[10px] text-slate-400">(click to exclude)</span>
            </div>

            {excludedLabels.length > 0 && (
              <button
                onClick={() => setExcludedLabels([])}
                className="px-1.5 py-0.5 text-[10px] text-teal-600 hover:bg-teal-50 rounded flex items-center gap-0.5"
              >
                <X size={10} /> Clear excluded
              </button>
            )}

            <div className="flex flex-wrap gap-1 ml-2">
              {allLabels.map(label => {
                const isExcluded = excludedLabels.includes(label);
                return (
                  <button
                    key={label}
                    onClick={() => toggleLabelExclusion(label)}
                    className={`px-2 py-0.5 text-[10px] rounded-full border transition-all ${
                      isExcluded
                        ? 'bg-teal-100 text-teal-600 border-teal-300 line-through'
                        : 'bg-white text-slate-600 border-slate-200 hover:border-slate-400'
                    }`}
                  >
                    {label}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      {loading ? (
        <div className="flex items-center justify-center py-16 bg-white rounded-lg border border-slate-200">
          <RefreshCw size={20} className="animate-spin text-teal-500" />
          <span className="ml-2 text-sm text-slate-600">Loading Anne Cole data...</span>
        </div>
      ) : error ? (
        <div className="flex items-center justify-center py-16 bg-white rounded-lg border border-teal-200">
          <AlertCircle size={20} className="text-teal-500 mr-2" />
          <span className="text-sm text-teal-600">{error}</span>
        </div>
      ) : (
        <>
          {/* Cross-filter indicator */}
          {hasCrossFilters && (
            <div className="bg-teal-50 border border-teal-200 rounded-lg px-4 py-2 flex items-center justify-between">
              <div className="flex items-center gap-3 flex-wrap">
                <span className="text-xs font-medium text-teal-700">Active Filters:</span>
                {selectedPresold.length > 0 && (
                  <div className="flex items-center gap-1">
                    <span className="text-[10px] text-teal-600">Presold:</span>
                    {selectedPresold.map(c => (
                      <span key={c} className="px-1.5 py-0.5 text-[10px] bg-teal-100 text-teal-700 rounded">{c}</span>
                    ))}
                  </div>
                )}
                {selectedOpenStock.length > 0 && (
                  <div className="flex items-center gap-1">
                    <span className="text-[10px] text-teal-600">Open Stock:</span>
                    {selectedOpenStock.map(c => (
                      <span key={c} className="px-1.5 py-0.5 text-[10px] bg-teal-100 text-teal-700 rounded">{c}</span>
                    ))}
                  </div>
                )}
                {selectedSsd.length > 0 && (
                  <div className="flex items-center gap-1">
                    <span className="text-[10px] text-teal-600">Swimsuit Direct:</span>
                    {selectedSsd.map(c => (
                      <span key={c} className="px-1.5 py-0.5 text-[10px] bg-rose-100 text-rose-700 rounded">{c === 'STOCK' ? 'Swimsuit Direct' : c}</span>
                    ))}
                  </div>
                )}
                {selectedStockCustomers.length > 0 && (
                  <div className="flex items-center gap-1">
                    <span className="text-[10px] text-teal-600">Stock Customer:</span>
                    {selectedStockCustomers.map(c => (
                      <span key={c} className="px-1.5 py-0.5 text-[10px] bg-blue-100 text-blue-700 rounded">{c}</span>
                    ))}
                  </div>
                )}
              </div>
              <button
                onClick={clearCrossFilters}
                className="px-2 py-1 text-xs text-teal-600 hover:bg-teal-100 rounded flex items-center gap-1"
              >
                <X size={12} /> Clear Filters
              </button>
            </div>
          )}

          <DataTable
            title="Presold (By Customer)"
            data={presoldData}
            groupByField="Customer"
            color="teal"
            selectedItems={selectedPresold}
            onItemClick={togglePresold}
          />
          {/* Swimsuit Direct Summary - Pivoted Table */}
          {ssdData.length > 0 && (
            <div className="bg-white rounded-lg border border-rose-200 shadow-sm">
              <div className="px-4 py-2 border-b border-rose-100 bg-rose-50">
                <h3 className="text-sm font-semibold text-rose-700">Swimsuit Direct Summary</h3>
              </div>
              <div className="overflow-x-auto">
                {(() => {
                  // Calculate totals for Swimsuit Direct
                  const totals = ssdData.reduce((acc, row) => {
                    acc.total += Number(row.Total_Production_Revenue) || 0;
                    acc.totalCost += Number(row.Total_Production_Cost) || 0;
                    acc.invoiced += Number(row.Invoiced_Revenue) || 0;
                    acc.invoicedCost += Number(row.Invoiced_Cost) || 0;
                    acc.pick += Number(row.On_Pick_Revenue) || 0;
                    acc.pickCost += Number(row.On_Pick_Cost) || 0;
                    acc.open += Number(row.Open_Confirmed_Revenue) || 0;
                    acc.openCost += Number(row.Open_Confirmed_Cost) || 0;
                    return acc;
                  }, { total: 0, totalCost: 0, invoiced: 0, invoicedCost: 0, pick: 0, pickCost: 0, open: 0, openCost: 0 });

                  const lts = totals.total - totals.invoiced;
                  const ltsCost = totals.totalCost - totals.invoicedCost;
                  const missing = lts - totals.pick - totals.open;
                  const missingCost = ltsCost - totals.pickCost - totals.openCost;

                  const calcGP = (rev, cost) => rev > 0 ? (rev - cost) / rev : 0;

                  return (
                    <table className="w-full text-xs">
                      <thead>
                        <tr className="bg-rose-50 border-b border-rose-100">
                          <th className="px-3 py-2 text-left font-semibold text-rose-700"></th>
                          <th className="px-3 py-2 text-right font-semibold text-rose-700">Total Prod</th>
                          <th className="px-3 py-2 text-right font-semibold text-rose-700">Invoiced</th>
                          <th className="px-3 py-2 text-right font-semibold text-rose-700">Pick</th>
                          <th className="px-3 py-2 text-right font-semibold text-rose-700">Open</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr className="border-b border-slate-100 bg-rose-50/50">
                          <td className="px-3 py-2 font-medium text-teal-600">Cost</td>
                          <td className="px-3 py-2 text-right tabular-nums text-teal-600">{formatCurrency(totals.totalCost)}</td>
                          <td className="px-3 py-2 text-right tabular-nums text-teal-600">{formatCurrency(totals.invoicedCost)}</td>
                          <td className="px-3 py-2 text-right tabular-nums text-teal-600">{formatCurrency(totals.pickCost)}</td>
                          <td className="px-3 py-2 text-right tabular-nums text-teal-600">{formatCurrency(totals.openCost)}</td>
                        </tr>
                        <tr>
                          <td className="px-3 py-2 font-medium text-slate-700">GP%</td>
                          <td className={`px-3 py-2 text-right tabular-nums ${gpColor(calcGP(totals.total, totals.totalCost))}`}>{formatPct(calcGP(totals.total, totals.totalCost))}</td>
                          <td className={`px-3 py-2 text-right tabular-nums ${gpColor(calcGP(totals.invoiced, totals.invoicedCost))}`}>{formatPct(calcGP(totals.invoiced, totals.invoicedCost))}</td>
                          <td className={`px-3 py-2 text-right tabular-nums ${gpColor(calcGP(totals.pick, totals.pickCost))}`}>{formatPct(calcGP(totals.pick, totals.pickCost))}</td>
                          <td className={`px-3 py-2 text-right tabular-nums ${gpColor(calcGP(totals.open, totals.openCost))}`}>{formatPct(calcGP(totals.open, totals.openCost))}</td>
                        </tr>
                      </tbody>
                    </table>
                  );
                })()}
              </div>
            </div>
          )}
          {/* Open Stock Summary - Pivoted Table */}
          {openStockData.length > 0 && (
            <div className="bg-white rounded-lg border border-cyan-200 shadow-sm">
              <div className="px-4 py-2 border-b border-cyan-100 bg-cyan-50">
                <h3 className="text-sm font-semibold text-cyan-700">Open Stock Summary</h3>
              </div>
              <div className="overflow-x-auto">
                {(() => {
                  // Calculate totals for Open Stock
                  const totals = openStockData.reduce((acc, row) => {
                    acc.total += Number(row.Total_Production_Revenue) || 0;
                    acc.totalCost += Number(row.Total_Production_Cost) || 0;
                    acc.invoiced += Number(row.Invoiced_Revenue) || 0;
                    acc.invoicedCost += Number(row.Invoiced_Cost) || 0;
                    acc.pick += Number(row.On_Pick_Revenue) || 0;
                    acc.pickCost += Number(row.On_Pick_Cost) || 0;
                    acc.open += Number(row.Open_Confirmed_Revenue) || 0;
                    acc.openCost += Number(row.Open_Confirmed_Cost) || 0;
                    return acc;
                  }, { total: 0, totalCost: 0, invoiced: 0, invoicedCost: 0, pick: 0, pickCost: 0, open: 0, openCost: 0 });

                  const lts = totals.total - totals.invoiced;
                  const ltsCost = totals.totalCost - totals.invoicedCost;
                  const missing = lts - totals.pick - totals.open;
                  const missingCost = ltsCost - totals.pickCost - totals.openCost;

                  const calcGP = (rev, cost) => rev > 0 ? (rev - cost) / rev : 0;

                  return (
                    <table className="w-full text-xs">
                      <thead>
                        <tr className="bg-cyan-50 border-b border-cyan-100">
                          <th className="px-3 py-2 text-left font-semibold text-cyan-700"></th>
                          <th className="px-3 py-2 text-right font-semibold text-cyan-700">Total Prod</th>
                          <th className="px-3 py-2 text-right font-semibold text-cyan-700">Invoiced</th>
                          <th className="px-3 py-2 text-right font-semibold text-cyan-700">Pick</th>
                          <th className="px-3 py-2 text-right font-semibold text-cyan-700">Open</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr className="border-b border-slate-100 bg-cyan-50/50">
                          <td className="px-3 py-2 font-medium text-teal-600">Cost</td>
                          <td className="px-3 py-2 text-right tabular-nums text-teal-600">{formatCurrency(totals.totalCost)}</td>
                          <td className="px-3 py-2 text-right tabular-nums text-teal-600">{formatCurrency(totals.invoicedCost)}</td>
                          <td className="px-3 py-2 text-right tabular-nums text-teal-600">{formatCurrency(totals.pickCost)}</td>
                          <td className="px-3 py-2 text-right tabular-nums text-teal-600">{formatCurrency(totals.openCost)}</td>
                        </tr>
                        <tr>
                          <td className="px-3 py-2 font-medium text-slate-700">GP%</td>
                          <td className={`px-3 py-2 text-right tabular-nums ${gpColor(calcGP(totals.total, totals.totalCost))}`}>{formatPct(calcGP(totals.total, totals.totalCost))}</td>
                          <td className={`px-3 py-2 text-right tabular-nums ${gpColor(calcGP(totals.invoiced, totals.invoicedCost))}`}>{formatPct(calcGP(totals.invoiced, totals.invoicedCost))}</td>
                          <td className={`px-3 py-2 text-right tabular-nums ${gpColor(calcGP(totals.pick, totals.pickCost))}`}>{formatPct(calcGP(totals.pick, totals.pickCost))}</td>
                          <td className={`px-3 py-2 text-right tabular-nums ${gpColor(calcGP(totals.open, totals.openCost))}`}>{formatPct(calcGP(totals.open, totals.openCost))}</td>
                        </tr>
                      </tbody>
                    </table>
                  );
                })()}
              </div>
            </div>
          )}
          <DataTable
            title="Stock By Customer (Invoiced To)"
            data={stockByCustomerData}
            groupByField="SalesCustomer"
            color="blue"
            selectedItems={selectedStockCustomers}
            onItemClick={toggleStockCustomer}
            salesOnly={true}
          />
        </>
      )}
    </div>
  );
}
